import React from 'react';
import { API } from 'aws-amplify';
import { toast } from 'react-toastify';
import { Checkbox, CircularProgress, Dialog, DialogContent } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import { canManageAiAdmin, isAiInlineSummarizationEnabled } from '@/shared/access';

const AI_ADDENDUM_URL = 'https://www.neogov.com/hubfs/AI%20Addendum%20V091024.pdf';
const FLAG_AI_SENTIMENT = 'AI_SENTIMENT_ANALYSIS_SETTING';
const FLAG_AI_SUMMARY = 'AI_SUMMARY_SETTING';
const FLAG_AI_SUMMARY_EXCLUDE_REPORTS = 'AI_SUMMARY_EXCLUDE_REPORTS';
const FLAG_AI_SUMMARY_EXCLUDE_SATISFACTION = 'AI_SUMMARY_EXCLUDE_SATISFACTION';

export default function AiAdministration() {
    const [loading, setLoading] = React.useState(true);
    const [loadError, setLoadError] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [aiSentimentEnabled, setAiSentimentEnabled] = React.useState(false);
    const [disclaimerOpen, setDisclaimerOpen] = React.useState(false);
    const [enableConfirmOpen, setEnableConfirmOpen] = React.useState(false);
    const [agreedToTerms, setAgreedToTerms] = React.useState(false);

    const [aiSummaryEnabled, setAiSummaryEnabled] = React.useState(false);
    const [summaryEnableConfirmOpen, setSummaryEnableConfirmOpen] = React.useState(false);
    const [summaryAgreedToTerms, setSummaryAgreedToTerms] = React.useState(false);
    const [summaryDisclaimerOpen, setSummaryDisclaimerOpen] = React.useState(false);
    const [savingSummary, setSavingSummary] = React.useState(false);
    const [excludeReports, setExcludeReports] = React.useState(false);
    const [excludeSatisfaction, setExcludeSatisfaction] = React.useState(false);
    const [savedExcludeReports, setSavedExcludeReports] = React.useState(false);
    const [savedExcludeSatisfaction, setSavedExcludeSatisfaction] = React.useState(false);
    const [savingExclusions, setSavingExclusions] = React.useState(false);

    const disclaimerCloseRef = React.useRef(null);
    const enableConfirmFirstRef = React.useRef(null);
    const summaryDisclaimerCloseRef = React.useRef(null);
    const summaryEnableFirstRef = React.useRef(null);

    React.useEffect(() => {
        if (disclaimerOpen) {
            setTimeout(() => disclaimerCloseRef.current?.focus(), 0);
        }
    }, [disclaimerOpen]);

    React.useEffect(() => {
        if (enableConfirmOpen) {
            setTimeout(() => enableConfirmFirstRef.current?.focus(), 0);
        }
    }, [enableConfirmOpen]);

    React.useEffect(() => {
        if (summaryDisclaimerOpen) {
            setTimeout(() => summaryDisclaimerCloseRef.current?.focus(), 0);
        }
    }, [summaryDisclaimerOpen]);

    React.useEffect(() => {
        if (summaryEnableConfirmOpen) {
            setTimeout(() => summaryEnableFirstRef.current?.focus(), 0);
        }
    }, [summaryEnableConfirmOpen]);

    React.useEffect(() => {
        if (!canManageAiAdmin()) {
            toast('You do not have permission to access AI Administration', { position: 'top-center', type: 'error' });
            setLoading(false);
            return;
        }
        API.get('Core', '/api/v1/ai-administration')
            .then(response => {
                const sentimentSetting = response.find(s => s.flag === FLAG_AI_SENTIMENT);
                setAiSentimentEnabled(sentimentSetting ? !!sentimentSetting.enabled : false);

                const summarySetting = response.find(s => s.flag === FLAG_AI_SUMMARY);
                setAiSummaryEnabled(summarySetting ? !!summarySetting.enabled : false);

                const exReports = response.find(s => s.flag === FLAG_AI_SUMMARY_EXCLUDE_REPORTS);
                const exReportsVal = exReports ? !!exReports.enabled : false;
                setExcludeReports(exReportsVal);
                setSavedExcludeReports(exReportsVal);

                const exSatisfaction = response.find(s => s.flag === FLAG_AI_SUMMARY_EXCLUDE_SATISFACTION);
                const exSatisfactionVal = exSatisfaction ? !!exSatisfaction.enabled : false;
                setExcludeSatisfaction(exSatisfactionVal);
                setSavedExcludeSatisfaction(exSatisfactionVal);
            })
            .catch(() => {
                toast('Could not load AI Administration settings', { position: 'top-center', type: 'error' });
                setLoadError(true);
            })
            .finally(() => setLoading(false));
    }, []);

    const callToggleApi = (newValue) => {
        setSaving(true);
        API.put('Core', '/api/v1/ai-administration', {
            body: { flag: FLAG_AI_SENTIMENT, enabled: newValue }
        })
            .then(() => {
                setAiSentimentEnabled(newValue);
                toast(
                    newValue
                        ? 'AI Sentiment Analysis has been enabled successfully.'
                        : 'AI Sentiment Analysis has been disabled.',
                    { position: 'top-center', type: 'success' }
                );
            })
            .catch(() => {
                toast('Could not update AI Sentiment Analysis setting', { position: 'top-center', type: 'error' });
            })
            .finally(() => setSaving(false));
    };

    const handleToggle = () => {
        if (!aiSentimentEnabled) {
            setAgreedToTerms(false);
            setEnableConfirmOpen(true);
        } else {
            callToggleApi(false);
        }
    };

    const handleEnableConfirm = () => {
        setEnableConfirmOpen(false);
        callToggleApi(true);
    };

    const handleCancelEnable = () => {
        setEnableConfirmOpen(false);
        setAgreedToTerms(false);
    };

    const callSummaryToggleApi = (newValue) => {
        setSavingSummary(true);
        API.put('Core', '/api/v1/ai-administration', {
            body: { flag: FLAG_AI_SUMMARY, enabled: newValue }
        })
            .then(() => {
                setAiSummaryEnabled(newValue);
                toast(
                    newValue
                        ? 'AI Summary has been enabled successfully.'
                        : 'AI Summary has been disabled.',
                    { position: 'top-center', type: 'success' }
                );
            })
            .catch(() => {
                toast('Could not update AI Summary setting', { position: 'top-center', type: 'error' });
            })
            .finally(() => setSavingSummary(false));
    };

    const handleSummaryToggle = () => {
        if (!aiSummaryEnabled) {
            setSummaryAgreedToTerms(false);
            setSummaryEnableConfirmOpen(true);
        } else {
            callSummaryToggleApi(false);
        }
    };

    const handleSummaryEnableConfirm = () => {
        setSummaryEnableConfirmOpen(false);
        callSummaryToggleApi(true);
    };

    const handleCancelSummaryEnable = () => {
        setSummaryEnableConfirmOpen(false);
        setSummaryAgreedToTerms(false);
    };

    const exclusionsDirty = excludeReports !== savedExcludeReports || excludeSatisfaction !== savedExcludeSatisfaction;

    const handleSaveExclusions = () => {
        setSavingExclusions(true);
        API.put('Core', '/api/v1/ai-administration/batch', {
            body: [
                { flag: FLAG_AI_SUMMARY_EXCLUDE_REPORTS, enabled: excludeReports },
                { flag: FLAG_AI_SUMMARY_EXCLUDE_SATISFACTION, enabled: excludeSatisfaction }
            ]
        })
            .then(() => {
                setSavedExcludeReports(excludeReports);
                setSavedExcludeSatisfaction(excludeSatisfaction);
                toast('Access exclusions saved successfully.', { position: 'top-center', type: 'success' });
            })
            .catch(() => {
                toast('Could not save access exclusions', { position: 'top-center', type: 'error' });
            })
            .finally(() => setSavingExclusions(false));
    };

    const handleCancelExclusions = () => {
        setExcludeReports(savedExcludeReports);
        setExcludeSatisfaction(savedExcludeSatisfaction);
    };

    if (loading) {
        return (
            <div className="aiAdminLoadingWrapper">
                <CircularProgress />
            </div>
        );
    }

    if (!canManageAiAdmin()) {
        return null;
    }

    if (loadError) {
        return null;
    }

    return (
        <div className="aiAdministrationView">

            <Dialog
                open={disclaimerOpen}
                onClose={() => setDisclaimerOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ className: 'aiDisclaimerDialog' }}
                style={{ zIndex: 1400 }}
                aria-labelledby="aiDisclaimerTitle"
            >
                <DialogContent className="aiDisclaimerModal">
                    <div className="aiDisclaimerTitleBar">
                        <span id="aiDisclaimerTitle" className="aiDisclaimerTitle">AI Disclaimer — AI Sentiment Analysis</span>
                        <div
                            ref={disclaimerCloseRef}
                            className="aiDisclaimerClose"
                            role="button"
                            aria-label="Close"
                            tabIndex={0}
                            onClick={() => setDisclaimerOpen(false)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDisclaimerOpen(false); } }}
                        >
                            <CloseIcon fontSize="small" />
                        </div>
                    </div>
                    <div className="aiDisclaimerBody">
                        <p>PowerEngage includes an AI-enabled feature (the "Sentiment Analyzer") that reviews feedback submitted to your agency and generates a suggested sentiment classification (e.g., positive, neutral, or negative). AI-generated sentiment classifications are provided for informational purposes only, may be inaccurate, and must be independently reviewed by users for appropriateness to your agency's context and the underlying feedback.</p>
                        <p>NEOGOV does not warrant or guarantee the accuracy, completeness, or reliability of any AI-generated sentiment classification, and disclaims liability for errors, inaccuracies, or omissions in such output, to the maximum extent permitted by law.</p>
                        <p>Users may change a sentiment classification at any time by selecting the "Sentiment" field and choosing the preferred classification.</p>
                        <p>By enabling or using the Sentiment Analyzer, you authorize NEOGOV to process the feedback data you submit to PowerEngage for the purpose of generating sentiment classifications using AI technology, and you acknowledge that such use is subject to the NEOGOV AI Addendum incorporated into the NEOGOV Services Agreement and Service Specifications.</p>
                    </div>
                    <div className="aiDisclaimerFooter">
                        <div
                            role="button"
                            tabIndex={0}
                            onClick={() => setDisclaimerOpen(false)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDisclaimerOpen(false); } }}
                            className="button confirm medium aiDisclaimerCloseBtn"
                        >
                            <div className="title">Close</div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog
                open={enableConfirmOpen}
                onClose={handleCancelEnable}
                maxWidth="sm"
                fullWidth
                PaperProps={{ className: 'aiDisclaimerDialog' }}
                style={{ zIndex: 1300 }}
                aria-labelledby="aiEnableConfirmTitle"
            >
                <DialogContent className="aiDisclaimerModal">
                    <div className="aiDisclaimerTitleBar">
                        <span id="aiEnableConfirmTitle" className="aiDisclaimerTitle">Enable AI Sentiment Analysis</span>
                    </div>
                    <div className="aiDisclaimerBody">
                        <p>This feature uses AI to instantly analyze and categorize the sentiment of incoming survey feedback, labeling responses as Positive, Negative, or Neutral.</p>
                        <p>
                            By enabling this feature, you agree to the terms of the{' '}
                            <span
                                ref={enableConfirmFirstRef}
                                className="aiAdminLink"
                                role="button"
                                tabIndex={0}
                                onClick={() => setDisclaimerOpen(true)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDisclaimerOpen(true); } }}
                            >AI Disclaimer</span>{' '}and{' '}
                            <a
                                href={AI_ADDENDUM_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="aiAdminLink"
                            >AI Addendum</a>.
                        </p>
                        <p>These terms supplement your existing agreement with NEOGOV.</p>
                        <p>By checking the box below and clicking &quot;Enable AI Feature,&quot; you:</p>
                        <ul className="aiEnableModalList">
                            <li>confirm you have authority to bind your organization</li>
                            <li>agree to the AI Disclaimer and AI Addendum</li>
                            <li>acknowledge the limitations of this AI feature</li>
                            <li>agree that you are responsible for reviewing and validating all output</li>
                        </ul>
                        <div className="aiEnableCheckboxRow">
                            <Checkbox
                                checked={agreedToTerms}
                                onChange={(e) => setAgreedToTerms(e.target.checked)}
                                id="aiAgreeCheckbox"
                                sx={{ padding: '0 8px 0 0', color: 'var(--colorTint)', '&.Mui-checked': { color: 'var(--colorTint)' } }}
                            />
                            <label htmlFor="aiAgreeCheckbox" className="aiEnableCheckboxLabel">I agree to the AI Terms</label>
                        </div>
                        <p className="aiEnableHelperText">If you do not agree, select &quot;Cancel.&quot;</p>
                    </div>
                    <div className="aiDisclaimerFooter aiEnableFooter">
                        <div
                            role="button"
                            tabIndex={0}
                            onClick={handleCancelEnable}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCancelEnable(); } }}
                            className="button dismiss medium aiEnableCancelBtn"
                        >
                            <div className="title">Cancel</div>
                        </div>
                        <div
                            role="button"
                            tabIndex={agreedToTerms ? 0 : -1}
                            aria-disabled={!agreedToTerms}
                            onClick={agreedToTerms ? handleEnableConfirm : undefined}
                            onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && agreedToTerms) { e.preventDefault(); handleEnableConfirm(); } }}
                            className={`button confirm medium aiEnableConfirmBtn ${!agreedToTerms ? 'disabled' : ''}`}
                        >
                            <div className="title">Enable AI Feature</div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <div className="aiAdminHeader">
                <div className="aiAdminHeaderRow">
                    <h2 className="aiAdminTitle">AI Administration</h2>
                    {isAiInlineSummarizationEnabled() && (
                        <div className="aiAdminHeaderActions">
                            <div
                                role="button"
                                tabIndex={exclusionsDirty && !savingExclusions ? 0 : -1}
                                aria-disabled={!exclusionsDirty || savingExclusions}
                                onClick={exclusionsDirty && !savingExclusions ? handleCancelExclusions : undefined}
                                onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && exclusionsDirty && !savingExclusions) { e.preventDefault(); handleCancelExclusions(); } }}
                                className={`button dismiss medium aiHeaderBtn ${!exclusionsDirty || savingExclusions ? 'disabled' : ''}`}
                            >
                                <div className="title">Cancel</div>
                            </div>
                            <div
                                role="button"
                                tabIndex={exclusionsDirty && !savingExclusions ? 0 : -1}
                                aria-disabled={!exclusionsDirty || savingExclusions}
                                onClick={exclusionsDirty && !savingExclusions ? handleSaveExclusions : undefined}
                                onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && exclusionsDirty && !savingExclusions) { e.preventDefault(); handleSaveExclusions(); } }}
                                className={`button confirm medium aiHeaderBtn ${!exclusionsDirty || savingExclusions ? 'disabled' : ''}`}
                            >
                                <div className="title">{savingExclusions ? 'Saving...' : 'Save'}</div>
                            </div>
                        </div>
                    )}
                </div>
                <p className="aiAdminSubtitle">
                    Configure AI features across your product. Enable or disable individual AI capabilities to control what's available to your users.
                </p>
            </div>

            <div className="aiAdminCard">
                <div className="aiAdminCardTitleRow">
                    <span className="aiAdminCardTitle">AI Sentiment Analysis</span>
                    <div className="aiAdminToggleWrapper">
                        <div
                            role="switch"
                            aria-checked={aiSentimentEnabled}
                            aria-label="AI Sentiment Analysis"
                            aria-disabled={saving}
                            tabIndex={saving ? -1 : 0}
                            onClick={saving ? undefined : handleToggle}
                            onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !saving) { e.preventDefault(); handleToggle(); } }}
                            className={`aiAdminSwitch ${aiSentimentEnabled ? 'switchOn' : 'switchOff'}`}
                        >
                            <div className={`accessory accessorySwitch ${aiSentimentEnabled ? 'on' : 'off'}`}>
                                <div className="switchThumb" />
                            </div>
                            <span className="aiAdminSwitchLabel">{aiSentimentEnabled ? 'On' : 'Off'}</span>
                        </div>
                    </div>
                </div>

                <p className="aiAdminCardDescription">
                    Uses AI to instantly analyze and categorize the sentiment of incoming survey feedback. By labeling responses—such as Positive, Negative, or Neutral—it helps users quickly gauge public perception and prioritize feedback that requires immediate attention.
                </p>

                <p className="aiAdminCardDescription">
                    Subject to the terms of the{' '}
                    <span
                        className="aiAdminLink"
                        role="button"
                        tabIndex={0}
                        onClick={() => setDisclaimerOpen(true)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDisclaimerOpen(true); } }}
                    >AI Disclaimer</span>{' '}and{' '}
                    <a
                        href={AI_ADDENDUM_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="aiAdminLink"
                    >AI Addendum <OpenInNewIcon style={{ fontSize: '12px', verticalAlign: 'middle' }} aria-hidden="true" /></a>.
                </p>
            </div>

            {isAiInlineSummarizationEnabled() && (
                <>
                    <Dialog
                        open={summaryDisclaimerOpen}
                        onClose={() => setSummaryDisclaimerOpen(false)}
                        maxWidth="sm"
                        fullWidth
                        PaperProps={{ className: 'aiDisclaimerDialog' }}
                        style={{ zIndex: 1400 }}
                        aria-labelledby="aiSummaryDisclaimerTitle"
                    >
                        <DialogContent className="aiDisclaimerModal">
                            <div className="aiDisclaimerTitleBar">
                                <span id="aiSummaryDisclaimerTitle" className="aiDisclaimerTitle">AI Disclaimer — AI Summary</span>
                                <div
                                    ref={summaryDisclaimerCloseRef}
                                    className="aiDisclaimerClose"
                                    role="button"
                                    aria-label="Close"
                                    tabIndex={0}
                                    onClick={() => setSummaryDisclaimerOpen(false)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSummaryDisclaimerOpen(false); } }}
                                >
                                    <CloseIcon fontSize="small" />
                                </div>
                            </div>
                            <div className="aiDisclaimerBody">
                                <p>PowerEngage includes an AI-enabled feature ("AI Summary") that generates concise summaries of content for quick review. AI-generated summaries are provided for informational purposes only, may be inaccurate, and must be independently reviewed by users for appropriateness to your agency's context.</p>
                                <p>NEOGOV does not warrant or guarantee the accuracy, completeness, or reliability of any AI-generated summary, and disclaims liability for errors, inaccuracies, or omissions in such output, to the maximum extent permitted by law.</p>
                                <p>Users can expand, regenerate, copy, or dismiss the summary at any time.</p>
                                <p>By enabling or using AI Summary, you authorize NEOGOV to process the data you submit to PowerEngage for the purpose of generating summaries using AI technology, and you acknowledge that such use is subject to the NEOGOV AI Addendum incorporated into the NEOGOV Services Agreement and Service Specifications.</p>
                            </div>
                            <div className="aiDisclaimerFooter">
                                <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setSummaryDisclaimerOpen(false)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSummaryDisclaimerOpen(false); } }}
                                    className="button confirm medium aiDisclaimerCloseBtn"
                                >
                                    <div className="title">Close</div>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>

                    <Dialog
                        open={summaryEnableConfirmOpen}
                        onClose={handleCancelSummaryEnable}
                        maxWidth="sm"
                        fullWidth
                        PaperProps={{ className: 'aiDisclaimerDialog' }}
                        style={{ zIndex: 1300 }}
                        aria-labelledby="aiSummaryEnableConfirmTitle"
                    >
                        <DialogContent className="aiDisclaimerModal">
                            <div className="aiDisclaimerTitleBar">
                                <span id="aiSummaryEnableConfirmTitle" className="aiDisclaimerTitle">Enable AI Summary</span>
                            </div>
                            <div className="aiDisclaimerBody">
                                <p>This feature provides a short AI-generated overview of content, helping users quickly understand key information. Users can expand, regenerate, copy, or dismiss the summary at any time.</p>
                                <p>
                                    By enabling this feature, you agree to the terms of the{' '}
                                    <span
                                        ref={summaryEnableFirstRef}
                                        className="aiAdminLink"
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setSummaryDisclaimerOpen(true)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSummaryDisclaimerOpen(true); } }}
                                    >AI Disclaimer</span>{' '}and{' '}
                                    <a
                                        href={AI_ADDENDUM_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="aiAdminLink"
                                    >AI Addendum</a>.
                                </p>
                                <p>These terms supplement your existing agreement with NEOGOV.</p>
                                <p>By checking the box below and clicking &quot;Enable AI Feature,&quot; you:</p>
                                <ul className="aiEnableModalList">
                                    <li>confirm you have authority to bind your organization</li>
                                    <li>agree to the AI Disclaimer and AI Addendum</li>
                                    <li>acknowledge the limitations of this AI feature</li>
                                    <li>agree that you are responsible for reviewing and validating all output</li>
                                </ul>
                                <div className="aiEnableCheckboxRow">
                                    <Checkbox
                                        checked={summaryAgreedToTerms}
                                        onChange={(e) => setSummaryAgreedToTerms(e.target.checked)}
                                        id="aiSummaryAgreeCheckbox"
                                        sx={{ padding: '0 8px 0 0', color: 'var(--colorTint)', '&.Mui-checked': { color: 'var(--colorTint)' } }}
                                    />
                                    <label htmlFor="aiSummaryAgreeCheckbox" className="aiEnableCheckboxLabel">I agree to the AI Terms</label>
                                </div>
                                <p className="aiEnableHelperText">If you do not agree, select &quot;Cancel.&quot;</p>
                            </div>
                            <div className="aiDisclaimerFooter aiEnableFooter">
                                <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={handleCancelSummaryEnable}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCancelSummaryEnable(); } }}
                                    className="button dismiss medium aiEnableCancelBtn"
                                >
                                    <div className="title">Cancel</div>
                                </div>
                                <div
                                    role="button"
                                    tabIndex={summaryAgreedToTerms ? 0 : -1}
                                    aria-disabled={!summaryAgreedToTerms}
                                    onClick={summaryAgreedToTerms ? handleSummaryEnableConfirm : undefined}
                                    onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && summaryAgreedToTerms) { e.preventDefault(); handleSummaryEnableConfirm(); } }}
                                    className={`button confirm medium aiEnableConfirmBtn ${!summaryAgreedToTerms ? 'disabled' : ''}`}
                                >
                                    <div className="title">Enable AI Feature</div>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>

                    <div className="aiAdminCard">
                        <div className="aiAdminCardTitleRow">
                            <span className="aiAdminCardTitle">AI Summary</span>
                            <div className="aiAdminToggleWrapper">
                                <div
                                    role="switch"
                                    aria-checked={aiSummaryEnabled}
                                    aria-label="AI Summary"
                                    aria-disabled={savingSummary}
                                    tabIndex={savingSummary ? -1 : 0}
                                    onClick={savingSummary ? undefined : handleSummaryToggle}
                                    onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !savingSummary) { e.preventDefault(); handleSummaryToggle(); } }}
                                    className={`aiAdminSwitch ${aiSummaryEnabled ? 'switchOn' : 'switchOff'}`}
                                >
                                    <div className={`accessory accessorySwitch ${aiSummaryEnabled ? 'on' : 'off'}`}>
                                        <div className="switchThumb" />
                                    </div>
                                    <span className="aiAdminSwitchLabel">{aiSummaryEnabled ? 'On' : 'Off'}</span>
                                </div>
                            </div>
                        </div>

                        <p className="aiAdminCardDescription">
                            Provides a short AI-generated overview of content, helping users quickly understand key information. Users can expand, regenerate, copy, or dismiss the summary at any time.
                        </p>

                        <p className="aiAdminCardDescription">
                            Subject to the terms of the{' '}
                            <span
                                className="aiAdminLink"
                                role="button"
                                tabIndex={0}
                                onClick={() => setSummaryDisclaimerOpen(true)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSummaryDisclaimerOpen(true); } }}
                            >AI Disclaimer</span>{' '}and{' '}
                            <a
                                href={AI_ADDENDUM_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="aiAdminLink"
                            >AI Addendum <OpenInNewIcon style={{ fontSize: '12px', verticalAlign: 'middle' }} aria-hidden="true" /></a>.
                        </p>

                        {aiSummaryEnabled && (
                            <div className="aiAdminExclusionsSection">
                                <h4 className="aiAdminExclusionsTitle">Access Exclusions</h4>
                                <p className="aiAdminExclusionsDescription">
                                    Select which modules should be excluded from AI Summary generation.
                                </p>
                                <div className="aiAdminExclusionRow">
                                    <Checkbox
                                        checked={excludeReports}
                                        onChange={(e) => setExcludeReports(e.target.checked)}
                                        id="excludeReportsCheckbox"
                                        disabled={savingExclusions}
                                        sx={{ padding: '0 8px 0 0', color: 'var(--colorTint)', '&.Mui-checked': { color: 'var(--colorTint)' } }}
                                        className="aiAdminExclusionCheckbox"
                                    />
                                    <label htmlFor="excludeReportsCheckbox" className="aiAdminExclusionLabel">Exclude Reports</label>
                                </div>
                                <div className="aiAdminExclusionRow">
                                    <Checkbox
                                        checked={excludeSatisfaction}
                                        onChange={(e) => setExcludeSatisfaction(e.target.checked)}
                                        id="excludeSatisfactionCheckbox"
                                        disabled={savingExclusions}
                                        sx={{ padding: '0 8px 0 0', color: 'var(--colorTint)', '&.Mui-checked': { color: 'var(--colorTint)' } }}
                                        className="aiAdminExclusionCheckbox"
                                    />
                                    <label htmlFor="excludeSatisfactionCheckbox" className="aiAdminExclusionLabel">Exclude Satisfaction</label>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
